import { useState } from "react";
import type { ConnectedFilter, JobMode, Station } from "../lib/api";

interface Props {
  stationsInBbox: Station[];
  stationFilter: ConnectedFilter;
  onCancel: () => void;
  onSubmit: (mode: JobMode, selectedStationId: number | null, filter: ConnectedFilter, useCache: boolean) => void;
}

export function ModePicker({ stationsInBbox, stationFilter, onCancel, onSubmit }: Props) {
  const [mode, setMode] = useState<JobMode>("union");
  const [selectedId, setSelectedId] = useState<number | null>(
    stationsInBbox[0]?.id ?? null,
  );
  const [filter, setFilter] = useState<ConnectedFilter>(stationFilter);
  const [useCache, setUseCache] = useState<boolean>(true);

  const canSubmit = mode === "union" || selectedId !== null;

  return (
    <div style={overlay}>
      <div style={modal}>
        <h2 style={{ marginTop: 0 }}>Configure coverage job</h2>
        <p style={{ color: "#aaa", fontSize: 13 }}>
          {stationsInBbox.length} stations inside bbox. Worker will also consider stations
          within 50 mi of the bbox edge.
        </p>

        <fieldset style={fs}>
          <legend>Aggregation</legend>
          <label style={opt}>
            <input type="radio" checked={mode === "union"} onChange={() => setMode("union")} />
            <span>
              <b>Union</b> — combined viewshed of all stations in scope (standard)
            </span>
          </label>
          <label style={opt}>
            <input type="radio" checked={mode === "per_station"} onChange={() => setMode("per_station")} />
            <span>
              <b>Per station</b> — viewshed for a single selected station
            </span>
          </label>
          {mode === "per_station" && (
            <select
              style={selectStyle}
              value={selectedId ?? ""}
              onChange={(e) => setSelectedId(+e.target.value)}
            >
              {stationsInBbox.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.identifier} {s.connected ? "●" : "○"}
                </option>
              ))}
            </select>
          )}
        </fieldset>

        <fieldset style={fs}>
          <legend>Station filter</legend>
          <label style={opt}>
            <input type="radio" checked={filter === "all"} onChange={() => setFilter("all")} />
            All stations
          </label>
          <label style={opt}>
            <input type="radio" checked={filter === "connected_only"} onChange={() => setFilter("connected_only")} />
            Connected only
          </label>
          <label style={opt}>
            <input type="radio" checked={filter === "disconnected_only"} onChange={() => setFilter("disconnected_only")} />
            Disconnected only
          </label>
        </fieldset>

        <fieldset style={fs}>
          <legend>Performance</legend>
          <label style={{ ...opt, alignItems: "center" }}>
            <input type="checkbox" checked={useCache} onChange={(e) => setUseCache(e.target.checked)} />
            <span>
              <b>Use cached viewsheds</b> when available — skips r.viewshed for stations
              already computed at the same AGL + DEM resolution + altitude tiers.
              Uncheck to force a fresh recompute.
            </span>
          </label>
        </fieldset>

        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={btnGhost}>
            Cancel
          </button>
          <button
            onClick={() => onSubmit(mode, mode === "per_station" ? selectedId : null, filter, useCache)}
            style={btnPrimary}
            disabled={!canSubmit}
          >
            Compute coverage
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100,
  display: "flex", alignItems: "center", justifyContent: "center",
};
const modal: React.CSSProperties = {
  background: "#141414", color: "#e5e5e5", padding: 24, minWidth: 460, maxWidth: 560,
  border: "1px solid #333", borderRadius: 6,
};
const fs: React.CSSProperties = { border: "1px solid #2a2a2a", borderRadius: 4, padding: 12, marginBottom: 12 };
const opt: React.CSSProperties = { display: "flex", alignItems: "start", gap: 8, fontSize: 13, margin: "4px 0" };
const selectStyle: React.CSSProperties = {
  marginTop: 6, width: "100%", background: "#1a1a1a", color: "#e5e5e5",
  border: "1px solid #333", padding: 6,
};
const btnPrimary: React.CSSProperties = {
  padding: "8px 14px", background: "#3a7bd5", color: "white", border: 0, borderRadius: 4, cursor: "pointer",
};
const btnGhost: React.CSSProperties = {
  padding: "8px 14px", background: "transparent", color: "#aaa", border: "1px solid #333", borderRadius: 4, cursor: "pointer",
};
